import { useState } from "react";
import { AlertTriangle, ArrowLeft, ScanLine } from "lucide-react";
import { blocks } from "../../store/state.js";
import { chunkCount } from "./DocumentTable";
import { t, tc } from "../../i18n/index.js";
import type { KbChunk, KbDocument } from "../../types/index.js";

const PAGE_SIZE = 20;

function passages(document: KbDocument): KbChunk[] {
  return (document.excerptChunks || []).map((chunk, index) =>
    typeof chunk === "string" ? { id: `c${index + 1}`, text: chunk } : chunk);
}

export default function DocumentDetail({ document, onBack, onOpenBlock }: {
  document: KbDocument; onBack: () => void; onOpenBlock: (blockId: string) => void;
}) {
  const [shown, setShown] = useState(PAGE_SIZE);
  const chunks = passages(document);
  const usedBy = blocks().filter(block => (block.sourceDocIds || []).includes(document.id));
  const stats = [
    { key: "pages", value: document.pageCount ?? "—" },
    { key: "passages", value: chunkCount(document) },
    { key: "characters", value: document.charCount ? document.charCount.toLocaleString("fr-FR") : "—" },
    { key: "usedBy", value: usedBy.length },
  ];

  return <>
    <header className="admin-head">
      <div>
        <div className="crumb">{t("admin.kb.crumb")}</div>
        <h1>{document.name}</h1>
        <p>{tc(`docCategory.${document.category}`, document.category)} · {tc(`orgUnit.${document.origin}`, document.origin)}</p>
      </div>
      <button className="btn outline" onClick={onBack}><ArrowLeft size={16} />{t("admin.detail.back")}</button>
    </header>

    <section className="admin-stats cols-4">
      {stats.map(stat => <article key={stat.key}>
        <strong>{stat.value}</strong><span>{t(`admin.kb.detail.${stat.key}`)}</span>
      </article>)}
    </section>

    {document.pagesWithoutText?.length ? <section className="notice warn">
      <strong><ScanLine size={16} aria-hidden="true" /> {t("admin.kb.detail.ocrTitle")}</strong>
      <p>{t("admin.kb.detail.ocrBody", { pages: document.pagesWithoutText.join(", ") })}</p>
    </section> : null}

    {document.exceedsDraftLimit && <section className="notice warn">
      <strong><AlertTriangle size={16} aria-hidden="true" /> {t("admin.kb.detail.limitTitle")}</strong>
      <p>{t("admin.kb.detail.limitBody")}</p>
    </section>}

    <section className="kb-usage">
      <h2>{t("admin.kb.detail.usedByTitle")}</h2>
      {usedBy.length
        ? <p className="chips">{usedBy.map(block => <button key={block.id} type="button" className="mini-pill"
            onClick={() => onOpenBlock(block.id)}>{block.code}</button>)}</p>
        : <p className="muted">{t("admin.kb.detail.usedByNone")}</p>}
    </section>

    <section className="kb-passages">
      <h2>{t("admin.kb.detail.passagesTitle")}</h2>
      <ol>
        {chunks.slice(0, shown).map(chunk => <li key={chunk.id}>
          <span className="kb-page-anchor">{chunk.page ? t("admin.kb.detail.page", { page: chunk.page }) : chunk.id}</span>
          <p>{chunk.text}</p>
        </li>)}
      </ol>
      {chunks.length > shown && <button className="btn outline" onClick={() => setShown(value => value + PAGE_SIZE)}>
        {t("admin.kb.detail.showMore", { remaining: chunks.length - shown })}
      </button>}
    </section>
  </>;
}
